import { useRef, useState } from "react";

const DAYS = [
  { code: "SU", short: "Sun", label: "Sunday" },
  { code: "MO", short: "Mon", label: "Monday" },
  { code: "TU", short: "Tue", label: "Tuesday" },
  { code: "WE", short: "Wed", label: "Wednesday" },
  { code: "TH", short: "Thu", label: "Thursday" },
  { code: "FR", short: "Fri", label: "Friday" },
  { code: "SA", short: "Sat", label: "Saturday" },
];

const WEEKDAY_FROM_INDEX = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

function defaultBydayFromDtstart(dtstart) {
  if (!(dtstart instanceof Date) || Number.isNaN(dtstart.getTime())) {
    return ["MO"];
  }
  return [WEEKDAY_FROM_INDEX[dtstart.getUTCDay()]];
}

function parseRrule(rrule, dtstart) {
  if (!rrule) {
    return { freq: "none", byday: defaultBydayFromDtstart(dtstart) };
  }
  const parts = Object.fromEntries(
    rrule
      .split(";")
      .map((kv) => kv.split("="))
      .filter((pair) => pair.length === 2)
      .map(([k, v]) => [k.trim().toUpperCase(), v.trim()])
  );
  const freqRaw = parts.FREQ;
  if (freqRaw === "WEEKLY") {
    const byday = parts.BYDAY
      ? parts.BYDAY.split(",").map((d) => d.trim().toUpperCase()).filter(Boolean)
      : defaultBydayFromDtstart(dtstart);
    const interval = parts.INTERVAL ? Number.parseInt(parts.INTERVAL, 10) : 1;
    return {
      freq: interval === 2 ? "biweekly" : "weekly",
      byday,
    };
  }
  if (freqRaw === "MONTHLY") {
    return { freq: "monthly", byday: defaultBydayFromDtstart(dtstart) };
  }
  return { freq: "none", byday: defaultBydayFromDtstart(dtstart) };
}

function parseUntil(rruleUntil) {
  if (!rruleUntil) return "";
  // Expected ISO like "2026-12-31T00:00:00.000Z"; pull the YYYY-MM-DD prefix.
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(rruleUntil);
  return match ? match[1] : "";
}

function buildRrule(freq, byday, dtstart) {
  if (freq === "none") return null;
  if (freq === "weekly") {
    return `FREQ=WEEKLY;BYDAY=${byday.join(",")}`;
  }
  if (freq === "biweekly") {
    return `FREQ=WEEKLY;INTERVAL=2;BYDAY=${byday.join(",")}`;
  }
  if (freq === "monthly") {
    const day =
      dtstart instanceof Date && !Number.isNaN(dtstart.getTime())
        ? dtstart.getUTCDate()
        : 1;
    return `FREQ=MONTHLY;BYMONTHDAY=${day}`;
  }
  return null;
}

export function RRuleForm({ value, dtstart, tzid: _tzid, onChange }) {
  // _tzid is intentionally unused in B6 — pass-through for forward-compat.
  void _tzid;

  const initialParsed = parseRrule(value?.rrule ?? null, dtstart);
  const [freq, setFreq] = useState(initialParsed.freq);
  const [byday, setByday] = useState(initialParsed.byday);
  const [untilDate, setUntilDate] = useState(parseUntil(value?.rruleUntil ?? null));

  // Sync local state from props during render when the parent-controlled
  // rrule / rruleUntil change externally. Using a ref + setState-during-render
  // is React's recommended "sync external prop to state" pattern (cheaper than
  // useEffect, avoids cascading-renders lint, and never emits onChange).
  const lastRruleRef = useRef(value?.rrule ?? null);
  const lastUntilRef = useRef(value?.rruleUntil ?? null);

  const currentRrule = value?.rrule ?? null;
  if (currentRrule !== lastRruleRef.current) {
    lastRruleRef.current = currentRrule;
    const parsed = parseRrule(currentRrule, dtstart);
    setFreq(parsed.freq);
    setByday(parsed.byday);
  }

  const currentUntil = value?.rruleUntil ?? null;
  if (currentUntil !== lastUntilRef.current) {
    lastUntilRef.current = currentUntil;
    setUntilDate(parseUntil(currentUntil));
  }

  function emit(nextFreq, nextByday, nextUntil) {
    const rrule = buildRrule(nextFreq, nextByday, dtstart);
    const rruleUntil = nextUntil ? `${nextUntil}T00:00:00.000Z` : null;
    if (onChange) onChange({ rrule, rruleUntil });
  }

  function handleFreqChange(e) {
    const nextFreq = e.target.value;
    let nextByday = byday;
    if (
      (nextFreq === "weekly" || nextFreq === "biweekly") &&
      (!byday || byday.length === 0)
    ) {
      nextByday = defaultBydayFromDtstart(dtstart);
    }
    // When switching from "none" into weekly/biweekly, ensure default byday
    // is seeded from dtstart even if local byday is stale.
    if (
      (nextFreq === "weekly" || nextFreq === "biweekly") &&
      freq === "none"
    ) {
      nextByday = defaultBydayFromDtstart(dtstart);
    }
    setFreq(nextFreq);
    setByday(nextByday);
    emit(nextFreq, nextByday, untilDate);
  }

  function toggleDay(code) {
    const next = byday.includes(code)
      ? byday.filter((d) => d !== code)
      : [...byday, code];
    setByday(next);
    emit(freq, next, untilDate);
  }

  function handleUntilChange(e) {
    const next = e.target.value; // "" or "YYYY-MM-DD"
    setUntilDate(next);
    emit(freq, byday, next);
  }

  const showByday = freq === "weekly" || freq === "biweekly";
  const showUntil = freq !== "none";

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
        Repeat
        <select
          aria-label="Repeat"
          value={freq}
          onChange={handleFreqChange}
          className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:bg-slate-800 dark:border-slate-700"
        >
          <option value="none">Doesn&rsquo;t repeat</option>
          <option value="weekly">Weekly</option>
          <option value="biweekly">Every other week</option>
          <option value="monthly">Monthly</option>
        </select>
      </label>

      {showByday && (
        <fieldset className="space-y-1">
          <legend className="text-xs font-medium text-slate-600 dark:text-slate-300">
            On
          </legend>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d) => (
              <label
                key={d.code}
                className="inline-flex items-center gap-1 text-xs"
              >
                <input
                  type="checkbox"
                  checked={byday.includes(d.code)}
                  onChange={() => toggleDay(d.code)}
                  aria-label={d.label}
                />
                {d.short}
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {showUntil && (
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
          Until
          <input
            type="date"
            aria-label="Until"
            value={untilDate}
            onChange={handleUntilChange}
            className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:bg-slate-800 dark:border-slate-700"
          />
        </label>
      )}

      <p className="text-xs font-mono text-slate-500 dark:text-slate-400">
        {value?.rrule || "(non-recurring)"}
      </p>
    </div>
  );
}

export default RRuleForm;
